/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import { Map, Set, List } from 'immutable';
import {
  useCallback, useEffect, useMemo, useReducer,
} from 'react';

/* ––
 * ––– Constants
 * –––––––––––––––––––––––––––––––– */
export const FlushStrategy = Object.freeze({
  Realtime:           'Realtime',
  OnThresholdReached: 'OnThresholdReached',
  Manual:             'Manual',
});

const defaultConfig = Object.freeze({
  flushBatchSize: 50,
  flushThreshold: 10,
});

const flushStrategies = Object.values(FlushStrategy);

/* ––
 * ––– Helper functions
 * –––––––––––––––––––––––––––––––– */
function shouldFlushQueue(queueKey, changedQueues, queues, threshhold) {
  return changedQueues.has(queueKey) && queues.get(queueKey).size >= threshhold;
}

function getUnprocessedSubstate(substate, consumedDigest, baseUndigestedSubstate) {
  return substate && substate.get('digest') !== consumedDigest
    ? substate
    : baseUndigestedSubstate.set('digest', Date.now());
}

/* ––
 * ––– State reducer
 * –––––––––––––––––––––––––––––––– */
const StateAction = Object.freeze({
  AddToQueues:          'AddToQueues',
  RemoveFromQueues:     'RemoveFromQueues',
  ScheduleFlush:        'ScheduleFlush',
  ClearRecordedChanges: 'ClearRecordedChanges',
  ClearScheduledFlush:  'ClearScheduledFlush',
  Reset:                'Reset',
});

const defaultCycleTrackingState = new Map({
  queues:   new Set(),
  metadata: new Map(),
});

const defaultState = new Map({
  queues: new Map({
    [FlushStrategy.Realtime]:           new Set(),
    [FlushStrategy.OnThresholdReached]: new Set(),
    [FlushStrategy.Manual]:             new Set(),
  }),
  recordedChanges: null,
  scheduledFlush:  null,
});

function stateReducer(state, action) {
  switch (action.type) {
    case StateAction.AddToQueues: {
      return action.queues.reduce(
        (patchedState, queueKey) => {
          // 1. Add the items to the queue
          const itemfullState = patchedState.updateIn(['queues', queueKey], (queue) => queue.union(action.items));

          // 2. Check if the queue has changed. If not, return the current state
          if (patchedState.getIn(['queues', queueKey]).size === itemfullState.getIn(['queues', queueKey]).size) {
            return itemfullState;
          }

          // 3. Update the recoredChanges sub-state with the queue key
          // (initialize a new substate if needed)
          return itemfullState.update(
            'recordedChanges',
            (recordedChanges) => getUnprocessedSubstate(
              recordedChanges,
              action.processingDigest,
              defaultCycleTrackingState,
            )
              .update('queues', (changedQueues) => changedQueues.add(queueKey))
              .update('metadata', (metadata) => metadata.merge(action.metadata)),
          );
        },
        state,
      );
    }
    case StateAction.RemoveFromQueues: {
      /**
       * Iterate over the provided queues and chain-patch the state
       * until we get a final value with all changes applied.
       */
      return action.queues.reduce(
        (patchedState, queueKey) => {
          // 1. Remove the items from the queue
          const itemlessState = patchedState.updateIn(['queues', queueKey], (queue) => queue.subtract(action.items));

          // 2. Check if the queue has changed. If not, return the current state
          if (patchedState.getIn(['queues', queueKey]).size === itemlessState.getIn(['queues', queueKey]).size) {
            return itemlessState;
          }

          // 3. Update the recoredChanges sub-state with the queue key
          // (initialize a new substate if needed)
          return itemlessState.update(
            'recordedChanges',
            (recordedChanges) => getUnprocessedSubstate(
              recordedChanges,
              action.processingDigest,
              defaultCycleTrackingState,
            ).update('queues', (changedQueues) => changedQueues.add(queueKey)),
          );
        },
        state,
      );
    }
    case StateAction.ScheduleFlush: {
      return state.update(
        'scheduledFlush',
        (scheduledFlush) => getUnprocessedSubstate(
          scheduledFlush,
          action.processingDigest,
          action.details,
        )
          .update('queues', (flushingQueues) => flushingQueues.union(action.details.get('queues')))
          .update('metadata', (metadata) => metadata.merge(action.details.get('metadata'))),
      );
    }
    case StateAction.ClearRecordedChanges: {
      if (!state.get('recordedChanges') || state.getIn(['recordedChanges', 'digest']) !== action.digest) {
        return state;
      }

      return state.set('recordedChanges', null);
    }
    case StateAction.ClearScheduledFlush: {
      if (!state.get('scheduledFlush') || state.getIn(['scheduledFlush', 'digest']) !== action.digest) {
        return state;
      }

      return state.set('scheduledFlush', null);
    }
    case StateAction.Reset: {
      return defaultState;
    }
    default: {
      throw new Error(`Invalid action type: ${action.type}`);
    }
  }
}

/* ––
 * ––– Hook
 * –––––––––––––––––––––––––––––––– */
function useUnpersistedResponsesTracker(key, onFlush, config) {
  /* –– Props processing
   * –––––––––––––––––––––––––––––––– */
  const { flushBatchSize, flushThreshold } = useMemo(
    () => ({ ...defaultConfig, ...config }),
    [config],
  );

  /* –– State declaration
   * –––––––––––––––––––––––––––––––– */
  const [state, dispatch] = useReducer(stateReducer, defaultState);

  const queues = state.get('queues');
  const recordedChanges = state.get('recordedChanges');
  const scheduledFlush = state.get('scheduledFlush');

  /* –– Handlers
   * –––––––––––––––––––––––––––––––– */
  /**
   * Metadata is only considered if the current addition triggers a flush
   */
  const trackResponse = useCallback((promptId, flushStrategy, flushMetadata = {}) => {
    if (!flushStrategies.includes(flushStrategy)) {
      return [false, `Invalid '${flushStrategy}' flush strategy`];
    }

    // Clear the response from all queues
    dispatch({
      type:             StateAction.RemoveFromQueues,
      items:            [promptId],
      queues:           flushStrategies,
      processingDigest: recordedChanges?.get('digest'),
    });

    // Add response to the queue (if not already there)
    dispatch({
      type:             StateAction.AddToQueues,
      items:            [promptId],
      queues:           [flushStrategy],
      metadata:         flushMetadata,
      processingDigest: recordedChanges?.get('digest'),
    });

    return [true, null];
  }, [recordedChanges]);

  const discardResponse = useCallback((promptId) => {
    // Remove response from all queues
    dispatch({
      type:             StateAction.RemoveFromQueues,
      items:            [promptId],
      queues:           flushStrategies,
      processingDigest: recordedChanges?.get('digest'),
    });
  }, [recordedChanges]);

  const flushResponses = useCallback(
    (requestedStrategies = [], metadata = {}) => {
      if (!Array.isArray(requestedStrategies)) {
        return [false, 'Requested strategies argument must be an array'];
      }

      if (!requestedStrategies.every((strategy) => flushStrategies.includes(strategy))) {
        return [false, 'There is an invalid strategy in the requested strategies'];
      }

      const strategiesToFlush = requestedStrategies.length ? requestedStrategies : flushStrategies;

      // Schedule the flush
      dispatch({
        type:    StateAction.ScheduleFlush,
        details: new Map({
          queues:   new Set(strategiesToFlush),
          metadata: new Map(metadata),
        }),
        processingDigest: scheduledFlush?.get('digest'),
      });

      return [true, null];
    },
    [scheduledFlush],
  );

  const generateResponseBatches = (includedQueues) => includedQueues
    .map((queueKey) => queues.get(queueKey))
    .reduce((joinedQueue, queue) => joinedQueue.union(queue), new Set())
    .toArray()
    .reduce((batches, promptId, index) => {
      const batchIndex = Math.floor(index / flushBatchSize);

      if (!batches.has(batchIndex)) {
        // eslint-disable-next-line no-param-reassign
        batches = batches.set(batchIndex, new List());
      }

      return batches.update(batchIndex, (batch) => batch.push(promptId));
    }, new List());

  /* –– Effects
   * –––––––––––––––––––––––––––––––– */
  useEffect(() => {
    if (scheduledFlush) {
      // Destructure the flush request
      const flushingQueues = scheduledFlush.get('queues');
      const flushMetadata = scheduledFlush.get('metadata');

      // Generate the response batches
      const responseBatches = generateResponseBatches(flushingQueues.toArray());

      // Run the onFlush callback
      onFlush(responseBatches, flushMetadata.toJS());

      // Clear the flushed responses from the queues
      dispatch({
        type:             StateAction.RemoveFromQueues,
        items:            responseBatches.flatten(true).toArray(),
        queues:           flushingQueues.toArray(),
        processingDigest: recordedChanges?.get('digest'),
      });

      // Remove the scheduled flush
      dispatch({ type: StateAction.ClearScheduledFlush, digest: scheduledFlush.get('digest') });
    }
  }, [scheduledFlush]);

  useEffect(() => {
    if (recordedChanges) {
      const changedQueues = recordedChanges.get('queues');
      let scheduledFlushDraft = defaultCycleTrackingState;

      if (shouldFlushQueue(FlushStrategy.Realtime, changedQueues, queues, 1)) {
        scheduledFlushDraft = scheduledFlushDraft
          .update('queues', (flushingQueues) => flushingQueues.add(FlushStrategy.Realtime));
      }

      if (
        shouldFlushQueue(FlushStrategy.OnThresholdReached, changedQueues, queues, flushThreshold)
      ) {
        scheduledFlushDraft = scheduledFlushDraft
          .update('queues', (flushingQueues) => flushingQueues.add(FlushStrategy.OnThresholdReached));
      }

      if (scheduledFlushDraft.get('queues').size) {
        scheduledFlushDraft = scheduledFlushDraft.set('metadata', recordedChanges.get('metadata'));

        dispatch({
          type:             StateAction.ScheduleFlush,
          details:          scheduledFlushDraft,
          processingDigest: scheduledFlush?.get('digest'),
        });
      }

      // Consume (clear) the recorded changes
      dispatch({ type: StateAction.ClearRecordedChanges, digest: recordedChanges.get('digest') });
    }
  }, [recordedChanges]);

  useEffect(() => { dispatch({ type: StateAction.Reset }); }, [key]);

  /* –– Hook exports
   * –––––––––––––––––––––––––––––––– */
  return useMemo(
    () => ({ trackResponse, discardResponse, flushResponses }),
    [trackResponse, discardResponse, flushResponses],
  );
}

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default useUnpersistedResponsesTracker;
